import { motion } from "framer-motion";

import type { ReportDeltas, ReportSummary } from "@/lib/api";
import { cn } from "@/lib/formatters";
import { useCountUp } from "@/lib/useCountUp";

interface ReportSummaryBarProps {
  summary: ReportSummary;
  deltas: ReportDeltas;
}

function fmtInt(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

function fmtPct(v: number): string {
  return `${(v * 100).toFixed(2)}%`;
}

type Hue = "cyan" | "magenta" | "violet" | "success";

interface Card {
  label: string;
  value: number;
  formatter: (v: number) => string;
  delta: number;
  deltaSuffix: string;
  invert?: boolean;
  hue: Hue;
}

export function ReportSummaryBar({ summary, deltas }: ReportSummaryBarProps) {
  const cards: Card[] = [
    { label: "clicks",       value: summary.clicks,      formatter: fmtInt,         delta: deltas.clicks_pct,      deltaSuffix: "%",  hue: "cyan" },
    { label: "impressions",  value: summary.impressions, formatter: fmtInt,         delta: deltas.impressions_pct, deltaSuffix: "%",  hue: "violet" },
    { label: "ctr",          value: summary.ctr,         formatter: fmtPct,         delta: deltas.ctr_pp,          deltaSuffix: " pp", hue: "magenta" },
    { label: "avg position", value: summary.position,    formatter: (v) => v.toFixed(1), delta: deltas.position_delta, deltaSuffix: "", hue: "success", invert: true },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {cards.map((c, i) => (
        <Cell key={c.label} card={c} index={i} prevWeek={deltas.prev_week} />
      ))}
    </div>
  );
}

function Cell({ card, index, prevWeek }: { card: Card; index: number; prevWeek: string }) {
  const animated = useCountUp(card.value);
  const positive = card.invert ? card.delta < 0 : card.delta > 0;
  const flat = card.delta === 0;

  const numClass =
    card.hue === "magenta" ? "num-hero-magenta text-magenta"
    : card.hue === "success" ? "num-hero-success text-success"
    : card.hue === "cyan" ? "num-hero text-primary"
    : "num-hero text-violet";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="surface surface-hoverable p-4 relative"
    >
      <span className={cn(
        "absolute top-3 right-3 w-1.5 h-1.5 rounded-full animate-pulse-led",
        flat ? "bg-muted" : positive ? "bg-success" : "bg-danger",
      )} />
      <p className="path-chip">{card.label}</p>
      <p className={cn("text-3xl font-mono mt-2", numClass)}>{card.formatter(animated)}</p>
      <p className={cn(
        "mt-1 text-xs font-mono",
        flat ? "text-muted" : positive ? "text-success" : "text-danger",
      )}>
        {flat
          ? "= 0"
          : `${card.delta > 0 ? "▲ +" : "▼ "}${Math.abs(card.delta).toFixed(card.deltaSuffix === " pp" ? 2 : 1)}${card.deltaSuffix}`}
        <span className="text-muted"> vs {prevWeek}</span>
      </p>
    </motion.div>
  );
}
